import React, { useEffect, useRef, useState } from "react";
import { Nav } from "components/ui/BootstrapCompat";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AiFillCaretDown,
  AiFillCaretLeft,
  AiFillCaretRight,
  AiFillCaretUp,
} from "react-icons/ai";
import { FiHeart, FiLogOut, FiMenu, FiUsers, FiX } from "react-icons/fi";
import { FaUniversity, FaWhatsapp } from "react-icons/fa";
import {
  MdHistory,
  MdPriceChange,
  MdSportsEsports,
  MdTune,
  MdWidgets,
} from "react-icons/md";
import { TbDashboardFilled } from "react-icons/tb";
import useLocalStorage from "helpers/useLocalStorage";
import { useStore } from "store/useStore";

type NavItem = {
  eventKey: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  subItems?: Array<{
    eventKey: string;
    title: string;
  }>;
};

type NavGroup = {
  group: string;
  items: NavItem[];
};

const navItems: NavGroup[] = [
  {
    group: "ເຄື່ອງມືພື້ນຖານ",
    items: [
      {
        eventKey: "/dashboard",
        title: "ແດສບອດ",
        icon: TbDashboardFilled,
      },
      {
        eventKey: "/donations",
        title: "ການບໍລິຈາກ",
        icon: FiHeart,
      },
      {
        eventKey: "/donations/widgets",
        title: "ປັບແຕ່ງວິດເຈັດ",
        icon: MdWidgets,
      },
      {
        eventKey: "/donations/customize",
        title: "ຂໍ້ມູນຊ່ອງສະຕຣີມ",
        icon: MdTune,
      },
      {
        eventKey: "/transaction",
        title: "ທຸລະກຳ",
        icon: MdHistory,
      },
      {
        eventKey: "/game",
        title: "ຈັດການເຕີມເກມ",
        icon: MdSportsEsports,
      },
    ],
  },
  {
    group: "ການຕັ້ງຄ່າ",
    items: [
      {
        eventKey: "/withdraw",
        title: "ແຈ້ງຖອນເງິນ",
        icon: MdPriceChange,
      },
      {
        eventKey: "/withdraw-history",
        title: "ປະຫວັດການຖອນ",
        icon: MdHistory,
      },
      {
        eventKey: "/merchant",
        title: "ຢືນຢັນໂຕຕົນ",
        icon: FaUniversity,
      },
      {
        eventKey: "/referrals",
        title: "ແນະນຳໝູ່",
        icon: FiUsers,
      },
    ],
  },
];

const LOGO_SRC = "/images/logo.png";
const WHATSAPP_SUPPORT_PHONE = "8562078344428";
const WHATSAPP_SUPPORT_MESSAGE = "ສະບາຍດີ! ຂ້ອຍຕ້ອງການຄວາມຊ່ວຍເຫຼືອກ່ຽວກັບແອັບ";
const mobilePrimaryEventKeys = [
  "/dashboard",
  "/donations",
  "/transaction",
  "/game",
];

const getMobileNavButtonClass = (isActive: boolean) =>
  `flex h-[58px] w-full min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 text-[10px] font-bold leading-tight transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 ${
    isActive
      ? "bg-[#eef7ff] text-[#168feb] dark:bg-primary-500/15 dark:text-primary-300"
      : "text-[#667085] hover:bg-slate-50 hover:text-[#168feb] dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-primary-300"
  }`;

function LogoMark({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <span className="block h-8 w-5 overflow-hidden" aria-label="OneTop">
        <img
          src={LOGO_SRC}
          alt=""
          className="h-8 w-auto max-w-none object-contain"
        />
      </span>
    );
  }

  return (
    <img
      src={LOGO_SRC}
      alt="OneTop"
      className="h-8 w-auto object-contain"
    />
  );
}

export default function SideBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLogin } = useStore();
  const [, setToken] = useLocalStorage("token", "");
  const [, setBusinessId] = useLocalStorage("businessId", "");
  const [activeKey, setActiveKey] = useState(location.pathname);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [popupSubmenu, setPopupSubmenu] = useState<{
    eventKey: string;
    position: { top: number; left: number };
  } | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const supportUrl = `https://wa.me/${WHATSAPP_SUPPORT_PHONE}?text=${encodeURIComponent(
    WHATSAPP_SUPPORT_MESSAGE,
  )}`;

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    setToken("");
    setBusinessId("");
    setIsLogin(false);
    navigate("/login");
  };

  const handleSelect = (eventKey: string | null) => {
    if (!eventKey) return;
    setActiveKey(eventKey);
    navigate(eventKey);
    setPopupSubmenu(null);
    setIsMobileMenuOpen(false);
  };

  const isItemActive = (eventKey: string) => {
    if (eventKey === "/donations") {
      return activeKey === eventKey;
    }

    if (activeKey === eventKey || activeKey.startsWith(`${eventKey}/`)) {
      return true;
    }

    return (
      eventKey === "/transaction" &&
      (activeKey.startsWith("/wallet") || activeKey.startsWith("/game-history"))
    );
  };

  const toggleSubMenu = (eventKey: string) => {
    if (isCollapsed) return;

    setExpandedItems((prev) =>
      prev.includes(eventKey)
        ? prev.filter((item) => item !== eventKey)
        : [...prev, eventKey],
    );
  };

  const handleItemClick = (item: NavItem, event?: React.MouseEvent) => {
    if (item.subItems?.length) {
      if (isCollapsed && event) {
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        setPopupSubmenu({
          eventKey: item.eventKey,
          position: {
            top: rect.top,
            left: rect.right + 8,
          },
        });
        return;
      }

      toggleSubMenu(item.eventKey);
      return;
    }

    handleSelect(item.eventKey);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setPopupSubmenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setActiveKey(location.pathname);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const allNavItems = navItems.flatMap((group) => group.items);
  const mobilePrimaryItems = allNavItems.filter((item) =>
    mobilePrimaryEventKeys.includes(item.eventKey),
  );
  const mobileMenuItems = allNavItems.filter(
    (item) => !mobilePrimaryEventKeys.includes(item.eventKey),
  );
  const isMobileMenuActive = mobileMenuItems.some((item) =>
    isItemActive(item.eventKey),
  );
  return (
    <>
      {isMobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-x-0 top-0 z-40 bg-slate-950/20 backdrop-blur-[1px] md:hidden"
            style={{ bottom: "calc(78px + env(safe-area-inset-bottom))" }}
            aria-label="Close mobile menu"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div
            className="fixed left-3 right-3 z-50 max-h-[calc(100vh-116px)] overflow-hidden rounded-lg border border-[#e6ebf1] bg-white shadow-[0_20px_60px_rgba(16,24,40,0.18)] dark:border-slate-800 dark:bg-slate-950 dark:shadow-[0_20px_60px_rgba(0,0,0,0.46)] md:hidden"
            style={{ bottom: "calc(82px + env(safe-area-inset-bottom))" }}
          >
            <div className="flex h-12 items-center justify-between border-b border-[#eef2f7] px-3 dark:border-slate-800">
              <span className="text-sm font-bold text-[#101828] dark:text-slate-100">
                ເມນູເພີ່ມເຕີມ
              </span>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#667085] transition hover:bg-slate-50 hover:text-[#168feb] dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-primary-300"
                aria-label="Close mobile menu"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <div className="grid max-h-[calc(100vh-244px)] grid-cols-2 gap-2 overflow-y-auto p-3">
              {mobileMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = isItemActive(item.eventKey);

                return (
                  <button
                    key={item.eventKey}
                    type="button"
                    className={`flex h-12 min-w-0 items-center gap-2 rounded-lg border px-3 text-left text-xs font-bold transition ${
                      isActive
                        ? "border-primary bg-[#eef7ff] text-[#168feb] dark:border-primary-500/50 dark:bg-primary-500/15 dark:text-primary-300"
                        : "border-[#eef2f7] bg-white text-[#667085] hover:border-primary hover:text-[#168feb] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400 dark:hover:border-primary-500/50 dark:hover:text-primary-300"
                    }`}
                    onClick={() => handleItemClick(item)}
                  >
                    <Icon className="h-5 w-5 flex-none" />
                    <span className="min-w-0 truncate">{item.title}</span>
                  </button>
                );
              })}
            </div>
            <div className="border-t border-[#eef2f7] p-3 dark:border-slate-800">
              <button
                type="button"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#fee4e2] bg-[#fff7f6] text-xs font-bold text-[#b42318] transition hover:bg-[#fee4e2] dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/15"
                onClick={handleLogout}
              >
                <FiLogOut className="h-4 w-4" />
                ອອກຈາກລະບົບ
              </button>
            </div>
          </div>
        </>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#e6ebf1] bg-white px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_rgba(16,24,40,0.08)] transition-colors dark:border-slate-800 dark:bg-slate-950 dark:shadow-[0_-8px_24px_rgba(0,0,0,0.32)] md:hidden">
        <Nav
          className="flex items-center gap-1"
          activeKey={activeKey}
          onSelect={handleSelect}
        >
          {mobilePrimaryItems.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item.eventKey);

            return (
              <Nav.Item key={item.eventKey} className="min-w-0 flex-1">
                <button
                  type="button"
                  className={getMobileNavButtonClass(isActive)}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => handleItemClick(item)}
                >
                  <Icon className="h-5 w-5 flex-none" />
                  <span className="max-w-full truncate text-center">
                    {item.title}
                  </span>
                </button>
              </Nav.Item>
            );
          })}
          <Nav.Item className="min-w-0 flex-1">
            <button
              type="button"
              className={getMobileNavButtonClass(
                isMobileMenuOpen || isMobileMenuActive,
              )}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            >
              {isMobileMenuOpen ? (
                <FiX className="h-5 w-5 flex-none" />
              ) : (
                <FiMenu className="h-5 w-5 flex-none" />
              )}
              <span className="max-w-full truncate text-center">ເມນູ</span>
            </button>
          </Nav.Item>
        </Nav>
      </div>

      <div className="hidden h-screen min-h-screen overflow-x-hidden border-r border-[#e6ebf1] bg-white transition-colors dark:border-slate-800 dark:bg-slate-950 md:block">
        <div
          ref={sidebarRef}
          className={`flex h-full flex-col overflow-x-hidden transition-all duration-300 ${
            isCollapsed ? "w-16" : "w-56"
          }`}
        >
          <div
            className={`flex h-[64px] items-center justify-between border-b border-[#e6ebf1] dark:border-slate-800 ${
              isCollapsed ? "px-1.5" : "px-4"
            }`}
          >
            {isCollapsed ? (
              <LogoMark compact />
            ) : (
              <LogoMark />
            )}
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#98a2b3] transition hover:bg-[#eef7ff] hover:text-[#168feb] dark:text-slate-500 dark:hover:bg-slate-900 dark:hover:text-primary-300"
              onClick={() => setIsCollapsed((prev) => !prev)}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <AiFillCaretRight /> : <AiFillCaretLeft />}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden py-3">
            <Nav
              className="flex flex-col gap-3 px-2"
              activeKey={activeKey}
              onSelect={handleSelect}
            >
              {navItems.map((group, groupIndex) => (
                <React.Fragment key={`group-${groupIndex}`}>
                  {!isCollapsed && (
                    <div className="mb-1 mt-3 first:mt-1">
                      <span className="px-3 text-[11px] font-bold uppercase text-[#98a2b3] dark:text-slate-500">
                        {group.group}
                      </span>
                    </div>
                  )}

                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isItemActive(item.eventKey);

                    return (
                      <React.Fragment key={item.eventKey}>
                        <Nav.Item>
                          <button
                            type="button"
                            className={`mb-2 flex h-11 w-full items-center rounded-lg px-3 text-sm transition last:mb-0 ${
                              isActive
                                ? "bg-[#168feb] font-bold text-white shadow-sm"
                                : "border border-[#dbe3ec] bg-white font-bold text-[#475467] hover:border-[#168feb] hover:bg-[#eef7ff] hover:text-[#168feb] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-primary-500 dark:hover:bg-slate-900 dark:hover:text-primary-300"
                            } ${isCollapsed ? "justify-center px-0" : ""}`}
                            onClick={(event) => handleItemClick(item, event)}
                            title={item.title}
                            aria-label={item.title}
                          >
                            <Icon
                              className={`h-5 w-5 flex-none ${
                                isActive
                                  ? "text-white"
                                  : "text-[#168feb]"
                              }`}
                            />
                            {!isCollapsed && (
                              <>
                                <span className="ms-3 min-w-0 flex-1 truncate text-left">
                                  {item.title}
                                </span>
                                {item.subItems?.length &&
                                  (expandedItems.includes(item.eventKey) ? (
                                    <AiFillCaretUp className="h-4 w-4 flex-none" />
                                  ) : (
                                    <AiFillCaretDown className="h-4 w-4 flex-none" />
                                  ))}
                              </>
                            )}
                          </button>
                        </Nav.Item>

                        {!isCollapsed && item.subItems?.length && (
                          <div
                            className={`overflow-hidden pl-6 transition-all duration-300 ${
                              expandedItems.includes(item.eventKey)
                                ? "max-h-40 opacity-100"
                                : "max-h-0 opacity-0"
                            }`}
                          >
                            {item.subItems.map((subItem) => (
                              <Nav.Item key={subItem.eventKey}>
                                <Nav.Link
                                  eventKey={subItem.eventKey}
                                  className="mb-2 flex h-10 w-full items-center rounded-lg border border-[#dbe3ec] bg-white px-3 text-sm font-bold text-[#475467] hover:border-[#168feb] hover:bg-[#eef7ff] hover:text-[#168feb] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-primary-500 dark:hover:bg-slate-900 dark:hover:text-primary-300"
                                >
                                  <span className="min-w-0 flex-1 truncate text-left">
                                    {subItem.title}
                                  </span>
                                </Nav.Link>
                              </Nav.Item>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </Nav>
          </div>

          <div className="space-y-2 border-t border-[#e6ebf1] p-2 dark:border-slate-800">
            <a
              className={`flex w-full items-center rounded-lg border border-[#d1fadf] bg-[#ecfdf3] text-[#067647] transition hover:border-[#a6f4c5] hover:bg-[#dcfae6] hover:text-[#05603a] dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/15 ${
                isCollapsed
                  ? "h-10 justify-center"
                  : "min-h-[86px] flex-col items-start justify-center gap-2 px-3 py-3"
              }`}
              href={supportUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Contact support on WhatsApp"
              title="WhatsApp Support"
            >
              <FaWhatsapp className="h-5 w-5 flex-none" aria-hidden="true" />
              {!isCollapsed && (
                <div className="min-w-0">
                  <p className="mb-1 text-sm font-bold leading-tight">
                    ຕິດຕໍ່ Support
                  </p>
                  <p className="mb-0 text-xs font-semibold leading-snug text-[#039855] dark:text-emerald-200">
                    WhatsApp
                  </p>
                </div>
              )}
            </a>
            <button
              type="button"
              className={`flex h-10 w-full items-center rounded-lg px-3 text-sm text-[#667085] transition hover:bg-slate-50 hover:text-[#168feb] dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-primary-300 ${
                isCollapsed ? "justify-center" : ""
              }`}
              onClick={handleLogout}
            >
              <FiLogOut className="h-5 w-5 flex-none text-[#98a2b3] dark:text-slate-500" />
              {!isCollapsed && (
                <span className="ms-3 flex-1 whitespace-nowrap text-left">
                  ອອກຈາກລະບົບ
                </span>
              )}
            </button>
          </div>
        </div>

        {isCollapsed && popupSubmenu && (
          <div
            className="fixed z-50 min-w-[160px] rounded-lg border border-[#e6ebf1] bg-white py-2 shadow-lg dark:border-slate-800 dark:bg-slate-950"
            style={{
              top: `${popupSubmenu.position.top}px`,
              left: `${popupSubmenu.position.left}px`,
            }}
          >
            {navItems
              .flatMap((group) =>
                group.items.filter(
                  (item) => item.eventKey === popupSubmenu.eventKey,
                ),
              )
              .flatMap((item) => item.subItems || [])
              .map((subItem) => (
                <Nav.Link
                  key={subItem.eventKey}
                  eventKey={subItem.eventKey}
                  className="flex cursor-pointer items-center p-2 text-[#667085] hover:bg-slate-50 hover:text-[#168feb] dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-primary-300"
                  onClick={() => {
                    handleSelect(subItem.eventKey);
                    setPopupSubmenu(null);
                  }}
                >
                  <span className="whitespace-nowrap px-3">
                    {subItem.title}
                  </span>
                </Nav.Link>
              ))}
          </div>
        )}
      </div>
    </>
  );
}
